import { useEffect, useRef } from 'react';
import gsap from 'gsap';

interface PageHeroProps {
  eyebrow: string;
  title: string;
  description: string;
  image: string;
}

export default function PageHero({ eyebrow, title, description, image }: PageHeroProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        el.querySelectorAll('.hero-line'),
        { y: 70, opacity: 0, filter: 'blur(18px)' },
        {
          y: 0,
          opacity: 1,
          filter: 'blur(0px)',
          duration: 1.6,
          stagger: 0.14,
          ease: 'power4.out',
          delay: 0.15,
        }
      );
      gsap.fromTo(
        el.querySelector('.hero-bg'),
        { scale: 1.12, filter: 'blur(20px)' },
        { scale: 1, filter: 'blur(0px)', duration: 2.4, ease: 'power3.out' }
      );
    }, el);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={ref}
      className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden bg-black text-white"
    >
      <div className="absolute inset-0">
        <img
          src={image}
          alt={title}
          className="hero-bg absolute inset-0 w-full h-full object-cover object-top opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-[#050505]" />
        <div className="absolute inset-0 tech-grid tech-grid-mask opacity-25" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="hero-line text-[11px] font-mono uppercase tracking-[0.3em] text-white/50 mb-6">
          {eyebrow}
        </div>
        <h1 className="hero-line font-display text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-normal leading-[1.02] max-w-5xl cursor-default">
          <span className="title-shimmer-auto inline-block">{title}</span>
        </h1>
        <p className="hero-line mt-8 text-base md:text-xl text-white/70 max-w-2xl leading-relaxed">
          {description}
        </p>
      </div>
    </section>
  );
}